<template>
	<div class="settings-wrapper notifications-wrapper">
		<BackToSettings />
		<div v-if="notificationsDisabled" class="notifications-disabled">
			<h2 class="title header-30" v-t="'settings_notification_prefs_title'" />
			<GroupMutedIcon class="muted-icon" />
			<div>
				{{ $t('settings_browser_notification_disabled') }}
			</div>
			<div>
				{{ $t('settings_browser_notifications_disabled_desc') }}
			</div>

			<Button v-if="!isDenied" primary @click="enableNotifications()">{{
				$t('common_button_enable')
			}}</Button>

			<Button
				class="notification-support-link"
				link
				href="http://support.pronto.io/en/articles/6479806-enabling-notifications-in-your-browser"
				v-if="isDenied"
			>
				{{ $t('settings_notifications_help_link') }}
			</Button>
		</div>

		<div
			v-if="!showNotificationSettings && !notificationsDisabled"
			class="settings-detail"
		>
			<h2 class="title header-30" v-t="'settings_notification_prefs_title'" />
			<NotifyMe />

			<div class="additional-notifications-section" style="margin-top: 50px;">
				<div class="additional-notifications-title">
					{{ $t('settings_notifications_section_additional') }}
				</div>

				<div class="additional-controls">
					<ToggleBox
						:title="$t('settings_notification_prefs_allow_message_reactions')"
						@checkedEvent="reactionsCheckedEvent"
						:toggleChecked="reactionsToggle"
						:loading="reactionsSpinner"
						data-cy="message-reactions"
					/>
				</div>
			</div>

			<div class="light-break"></div>

			<div class="additional-notifications-section">
				<div class="additional-notifications-title">
					{{ $t('groupsettings_options_title') }}
				</div>

				<div class="additional-controls">
					<ToggleBox
						title="Play Notification Sounds in Web Browser"
						@checkedEvent="soundCheckedEvent"
						:toggleChecked="soundToggle"
						:spinner="soundSpinner"
						data-cy="play-notifications"
					/>
				</div>
			</div>

			<div class="light-break"></div>

			<div class="overrides">
				<span class="override-title">{{
					$t('settings_notifications_title_group_overrides')
				}}</span>
				<div class="add-override" @click="showOverrideGroups = true">
					{{ $t('settings_notifications_add_override') }}
				</div>
			</div>

			<div class="override-list">
				<div
					class="override-item"
					v-for="override in currentOverrides"
					@click.stop="selected_bubble = override.bubble_id"
					:key="override.bubble_id"
				>
					<div class="category-and-group">
						<div class="category-name">{{ override.category }}</div>
						<div class="group-name">{{ override.title }}</div>
					</div>
					<div class="override-name">
						{{ getPreferenceName(override.notificationpreference) }}
					</div>
					<div
						class="remove-override"
						@click.stop="removeOverride(override.bubble_id)"
					>
						<CloseIcon />
					</div>
				</div>
			</div>

			<div class="no-overrides" v-if="currentOverrides.length === 0">
				{{ $t('settings_notifications_different_notifications') }}
			</div>

			<SimpleModal
				v-if="showOverrideGroups"
				@close="showOverrideGroups = false"
			>
				<div class="override-wrapper">
					<div class="modal-title">
						{{ $t('settings_notifications_title_select_group') }}
					</div>
					<div class="group-list">
						<div v-for="(group, index) in groups" :key="group.id">
							<div v-if="showCategory(index, group.category)" class="category">
								{{ group.category }}
							</div>
							<div
								class="group-title"
								style="outline: 0;"
								@click="selected_bubble = group.id"
							>
								{{ group.title }}
							</div>
						</div>
					</div>
				</div>
			</SimpleModal>
		</div>

		<GroupNotifications
			v-if="showNotificationSettings"
			:prop_bubble_id="selected_bubble"
			@close="selected_bubble = null"
		/>
	</div>
</template>

<script>
import store, {
	getMyMembershipForBubble,
	getDMChats,
	getChatsWithNoCategory,
	getChatsForCategory,
	getCategories,
	getChats,
	getCategory,
} from '@/store'
import NotifyMe from '@/components/NotifyMe'
import ToggleBox from '@/components/ToggleBox'
import GroupNotifications from '@/components/GroupSettings/GroupNotifications'
import { SimpleModal, Button } from '@/components/common'
import { post } from '@/helpers/api'
import { sortBubblesByTitle } from '@/helpers/utils'
import BackToSettings from './BackToSettings'

import { CloseIcon, GroupMutedIcon } from '@/icons'

export default {
	components: {
		NotifyMe,
		ToggleBox,
		GroupNotifications,
		SimpleModal,
		CloseIcon,
		GroupMutedIcon,
		Button,
		BackToSettings,
	},

	data() {
		return {
			store,
			reactionsSpinner: false,
			soundSpinner: false,
			selected_bubble: null,
			showNotificationSettings: false,
			showOverrideGroups: false,
		}
	},

	watch: {
		selected_bubble: function (val) {
			if (val) {
				this.showNotificationSettings = true
			} else {
				this.showNotificationSettings = false
				this.showOverrideGroups = false
			}
		},
	},

	computed: {
		currentOverrides() {
			return this.getAllOverrides()
		},

		categories() {
			return getCategories()
				.map((cat) => {
					return getChatsForCategory(cat.id)
						.map((bubble) =>
							this.assignCategoryAndName(
								bubble,
								cat.displayname,
								bubble.displayname
							)
						)
						.sort(sortBubblesByTitle)
				})
				.flat()
		},

		noncategories() {
			return getChatsWithNoCategory()
				.map((bubble) =>
					this.assignCategoryAndName(
						bubble,
						this.$t('chat_drawer_section_bubbles_uncatagorized'),
						bubble.displayname
					)
				)
				.slice()
				.sort(sortBubblesByTitle)
		},

		dms() {
			return getDMChats()
				.map((bubble) =>
					this.assignCategoryAndName(
						bubble,
						this.$t('chat_drawer_section_directmessages'),
						bubble.displayname
					)
				)
				.slice()
				.sort(sortBubblesByTitle)
		},

		groups() {
			return [...this.categories, ...this.noncategories, ...this.dms]
		},

		reactionsToggle() {
			return this.store.auth.user.notificationreactions
		},

		soundToggle() {
			return this.store.auth.user.webnotificationsoundsenabled
		},

		notificationsDisabled() {
			return this.store.notificationPermission !== 'granted'
		},

		isDenied() {
			return this.store.notificationPermission === 'denied'
		},
	},

	methods: {
		getAllOverrides() {
			const chats = getChats().filter(
				(c) =>
					getMyMembershipForBubble(c.id).notificationpreference !==
					'DEFAULT_INHERIT'
			)

			return chats.map((c) => {
				let category

				if (c.category_id) {
					category = getCategory(c.category_id).displayname
				} else if (c.isdm) {
					category = 'Direct Message'
				} else {
					category = 'Other Group'
				}

				return {
					bubble_id: c.id,
					category,
					title: c.displayname,
					notificationpreference: getMyMembershipForBubble(c.id)
						.notificationpreference,
				}
			})
		},

		assignCategoryAndName(bubble, category, title) {
			return { id: bubble.id, category, title }
		},

		getPreferenceName(notificationpreference) {
			const options = {
				ALL: this.$t('settings_notification_prefs_all_messages'),
				MENTIONS: this.$t('settings_notification_prefs_only_mentions'),
				NONE: this.$t('settings_notification_prefs_nothing'),
			}

			const re = new RegExp(Object.keys(options).join('|'), 'ig')
			const [pref] = notificationpreference.match(re) || []

			return pref ? options[pref] : options['ALL']
		},

		async reactionsCheckedEvent(bool) {
			const payload = {
				notificationreactions: bool,
			}

			this.reactionsSpinner = true

			try {
				const { data } = await post('/api/v1/user.update', payload)
				this.store.auth.user.notificationreactions =
					data.user.notificationreactions
				this.$alert.success(this.$t('common_confirmation_updated'))
			} catch (e) {
				console.error(e)
				this.$alert.error()
			}

			this.reactionsSpinner = false
		},

		async soundCheckedEvent(bool) {
			const payload = {
				webnotificationsoundsenabled: bool,
			}

			this.soundSpinner = true

			try {
				const { data } = await post('/api/v1/user.update', payload)
				this.store.auth.user.webnotificationsoundsenabled =
					data.user.webnotificationsoundsenabled
				this.$alert.success(this.$t('common_confirmation_updated'))
			} catch (e) {
				console.error(e)
				this.$alert.error()
			}

			this.soundSpinner = false
		},

		async removeOverride(id) {
			const payload = {
				bubble_id: id,
				notificationpreference: 'DEFAULT_INHERIT',
				meetings: true,
			}

			let membership = getMyMembershipForBubble(id)

			let preference = membership.notificationpreference

			membership.notificationpreference = 'DEFAULT_INHERIT'

			try {
				await post('/api/v1/membership.update', payload)
			} catch (e) {
				console.error(e)
				membership.notificationpreference = preference
				this.$alert.error()
			}
		},

		showCategory(idx, category) {
			const first_index_of_category = this.groups.findIndex(
				(item) => item.category === category
			)
			return first_index_of_category === idx
		},

		enableNotifications() {
			EventBus.$emit('enableNotifications')
		},
	},
}
</script>

<style scoped lang="scss">
@import 'src/sass/variables';

.notifications-wrapper {
	position: relative;
	overflow-y: auto;
}

.settings-detail {
	padding-bottom: 60px;
}

.title {
	margin: 0 auto;
	text-align: center;
}

:deep(.togglebox) {
	padding-right: 25px;
}

:deep(.more-padding) {
	padding-top: 10px;
	padding-bottom: 10px;
}

.overrides {
	position: relative;
	display: flex;
	align-content: space-between;
	margin-top: 60px;
	margin-bottom: 30px;
}

.override-title {
	font-family: Avenir-Heavy;
	font-size: 18px;
	flex: 1;
}

.add-override {
	font-family: Avenir-Medium;
	font-size: 16px;
	color: var(--blue-300);
	cursor: pointer;
}

.override-wrapper {
	height: 550px;
	width: 500px;
}

.override-item {
	display: flex;
	width: calc(100% + 30px);
	border-radius: 12px;
	cursor: pointer;
	&:hover {
		background-color: var(--neutral-200);
		.remove-override {
			opacity: 1;
		}
	}
}

.category-and-group {
	flex: 1;
	padding-left: 16px;
	padding-top: 12px;
	padding-bottom: 12px;
}

.override-name {
	font-family: Avenir-Medium;
	font-size: 14px;
	margin-top: 10px;
	padding-top: 12px;
}

.remove-override {
	font-family: Avenir;
	font-size: 12px;
	padding: 12px 14px 12px 20px;
	opacity: 0;
	color: #9498a3;
}

.remove-override :deep(svg) {
	margin-top: 12px;
	width: 14px;
}

.remove-override:hover :deep(svg) {
	--color: $pronto-blue !important;
}

.modal-title {
	font-family: Avenir-Heavy;
	font-size: 20px;
	text-align: center;
	margin-top: 20px;
	margin-bottom: 14px;
}

.group-list {
	overflow: auto;
	height: calc(100% - 80px);
}

.category {
	font-family: Avenir-Heavy;
	font-size: 14px;
	padding: 8px 25px;
	margin-top: 10px;
	margin-bottom: 10px;
	background: var(--neutral-200);
	@include ellipsis;
}

.group-title {
	cursor: pointer;
	width: 100%;
	font-family: Avenir-Medium;
	font-size: 17px;
	padding: 8px 25px;
	margin-left: 0;
	@include ellipsis;
	&:hover {
		color: var(--blue-300);
	}
}

.category-name {
	font-family: Avenir;
	font-size: 10px;
	margin-bottom: 3px;
}

.group-name {
	font-family: Avenir-Heavy;
	font-size: 15px;
}

:deep(.group-notifications-wrapper) {
	position: absolute;
	top: 0;
	right: 0;
	left: 0;
	bottom: 0;
	display: block;
	padding: 0 20px;
}

.no-overrides {
	font-family: Avenir-Medium;
	font-size: 14px;
	text-align: center;
	color: #9498a3;
	margin-top: 70px;
}

.light-break {
	border-bottom: 1px solid var(--neutral-40040);
	margin-top: 40px;
	margin-bottom: 40px;
	padding-top: 10px;
}

.additional-notifications-section {
	display: flex;
}

.additional-notifications-title {
	flex: 1;
	height: 25px;
	margin-right: 10px;
	margin-top: 12px;
	font-family: Avenir-Heavy;
	font-size: 18px;
}

.additional-controls {
	flex: 2;
	max-width: 500px;
}

.additional-controls :deep(.success) {
	top: 8px;
}

.notifications-disabled {
	text-align: center;
	margin-left: -73px;
	margin-top: 115px;
	color: var(--neutral-600);
	font-size: 18px;
	font-family: map-get($fonts, 300);
	.muted-icon {
		height: 50px;
		width: 40px;
	}
	:nth-child(2) {
		font-size: 24px;
		font-family: map-get($fonts, 500);
		margin: 10px 0 20px 0;
	}
	button {
		margin-top: 30px;
	}
	.notification-support-link {
		margin-top: 35px;
		font-size: 18px;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.override-wrapper {
		.group-list {
			.category {
				background: var(--neutral-0);
			}
			.group-title {
				&:hover {
					color: var(--teal-300);
				}
			}
		}
	}
}
</style>
